"use client";

import { useId, useState } from "react";
import { m } from "motion/react";
import { Icon } from "@/components/icon";
import { duration, easing } from "@/lib/animation/tokens";
import type { FaqItemProps } from "./faq-item.interface";

/**
 * FAQ item — controlled accordion with smooth expand/collapse.
 *
 * Previously used native `<details>/<summary>`, which snaps instantly
 * with no transition. Switched to a button + animated panel so the
 * height can interpolate from 0 → auto. ARIA still satisfies the
 * accordion pattern: `aria-expanded` on the button, `aria-controls`
 * referencing the panel, and `aria-hidden` toggled on the panel.
 */
export function FaqItem({ question, answer }: FaqItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const reactId = useId();
  const panelId = `${reactId}-panel`;
  const buttonId = `${reactId}-trigger`;

  return (
    <div className="border-hop border-b">
      <button
        id={buttonId}
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((o) => !o)}
        className="flex w-full cursor-pointer items-center justify-between gap-4 py-6 text-left"
      >
        <span className="text-hop text-[18px] font-bold">{question}</span>
        <span className="bg-blush-lambic text-hop relative flex h-6 w-6 shrink-0 items-center justify-center rounded">
          {/* Two icons stacked + cross-faded. The plus also rotates a
              touch as it fades, which softens the swap. */}
          <m.span
            className="absolute flex items-center justify-center"
            animate={{ opacity: isOpen ? 1 : 0 }}
            transition={{ duration: duration.xs, ease: easing.out }}
            aria-hidden
          >
            <Icon name="minus" className="h-0.5 w-3.5" />
          </m.span>
          <m.span
            className="absolute flex items-center justify-center"
            animate={{ opacity: isOpen ? 0 : 1, rotate: isOpen ? 90 : 0 }}
            transition={{ duration: duration.xs, ease: easing.out }}
            aria-hidden
          >
            <Icon name="plus" className="h-3.5 w-3.5" />
          </m.span>
        </span>
      </button>
      <m.div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        aria-hidden={!isOpen}
        initial={false}
        animate={{ height: isOpen ? "auto" : 0 }}
        transition={{ duration: duration.md, ease: easing.out }}
        style={{ overflow: "hidden" }}
      >
        <p className="text-hop pt-3 pb-9 text-base font-light">{answer}</p>
      </m.div>
    </div>
  );
}
